import {isUndefined} from 'lodash';
import {RgbColor} from 'polished/lib/types/color';

import {globalDocument} from '../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {colorIsEqual, tryParseColor} from '../../../../../libs/shared/core/src/models/helpers/colorHelpers';
import {formatFontFamily, parseFontFamily} from './fontFamilyHelpers';
import {formatFontSize, parseFontSize} from './fontSizeHelpers';
import {defaultHtmlStyle, HtmlStyle} from './htmlStyle';

/*
 * Sanitizer.
 */

/** Prepares the default font style user preference for use as inline style. */
export function sanitizeDefaultFontStyle(defaultFontStyle: string): string {
  // Parse the CSS.
  const element = globalDocument.createElement('span');
  const {style} = element;
  style.cssText = defaultFontStyle;

  // Ignore Old Front style.
  if (hasLegacyDefaultFontStyle(element)) {
    return '';
  }

  // Keep only color, font family, and font size.
  const {color, fontFamily, fontSize} = style;
  style.cssText = '';
  style.color = color;
  style.fontFamily = fontFamily;
  style.fontSize = fontSize;
  return style.cssText;
}

const legacyDefaultTextColors: ReadonlyArray<RgbColor> = [
  {
    red: 0,
    blue: 0,
    green: 0,
  },
  {
    // '#212121'
    red: 33,
    blue: 33,
    green: 33,
  },
];

/** Determines if the given element has Old Front styling. */
function hasLegacyDefaultFontStyle(element: HTMLElement): boolean {
  const {color, fontFamily, fontSize} = element.style;
  const parsedColor = tryParseColor(color);

  const colorMatch = Boolean(
    parsedColor && legacyDefaultTextColors.some((legacyColor) => colorIsEqual(parsedColor, legacyColor)),
  );
  const fontMatch = Boolean(fontFamily && fontFamily.includes('Helvetica'));
  return colorMatch && fontMatch && fontSize === '13px';
}

/*
 * Deserializer.
 */

/** Deserialize the default font style preference for formatting toolbar indication. */
export function deserializeDefaultFontStyle(defaultFontStyle: string): HtmlStyle {
  const element = globalDocument.createElement('span');
  element.style.cssText = defaultFontStyle;

  return {
    ...defaultHtmlStyle,
    textColor: element.style.color || defaultHtmlStyle.textColor,
    fontFamily: parseFontFamily(element.style.fontFamily || '') || defaultHtmlStyle.fontFamily,
    fontSize: parseFontSize(element.style.fontSize || '') || defaultHtmlStyle.fontSize,
  };
}

/*
 * Serializer.
 */

/** Serialize formatting toolbar indication for the default font style preference. */
export function serializeDefaultFontStyle(htmlStyle: HtmlStyle): string {
  const element = globalDocument.createElement('span');

  element.style.color = serializeHtmlStyleColorValue(htmlStyle.textColor, defaultHtmlStyle.textColor) || '';

  element.style.fontFamily = serializeHtmlStyleValue(
    htmlStyle.fontFamily,
    defaultHtmlStyle.fontFamily,
    formatFontFamily,
  );

  element.style.fontSize = serializeHtmlStyleValue(
    htmlStyle.fontSize,
    defaultHtmlStyle.fontSize,
    formatFontSize,
  );

  return element.style.cssText;
}

function serializeHtmlStyleValue<T>(
  value: T | undefined,
  defaultValue: T | undefined,
  formatter: (value: T) => string,
): string {
  if (isUndefined(value) || value === defaultValue) {
    return '';
  }

  return formatter(value);
}

function serializeHtmlStyleColorValue(
  value: string | undefined,
  defaultValue: string | undefined,
): string | null {
  if (isUndefined(value)) {
    return null;
  }

  const color = tryParseColor(value);
  const defaultColor = tryParseColor(defaultValue);
  if (!color || !defaultColor || colorIsEqual(color, defaultColor)) {
    return null;
  }

  return value;
}
